(() => {
  if (customElements.get('shipping-reward')) return;

  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;

  class ShippingReward extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.thresholdCents || 0);
      this.protectionVariantId = Number(this.dataset.protectionVariantId || 0);
      this.currency = this.dataset.currencyCode || 'AUD';
      this.locale = this.dataset.locale || document.documentElement.lang || 'en-AU';

      this.beforeMessage = this.querySelector('[data-shipping-reward-before]');
      this.reachedMessage = this.querySelector('[data-shipping-reward-reached]');
      this.remainingAmount = this.querySelector('[data-shipping-reward-remaining]');
      this.progress = this.querySelector('[data-shipping-reward-progress]');
      this.fill = this.querySelector('[data-shipping-reward-fill]');

      this.sync();
    }

    isExcludedItem(item) {
      const isProtectionVariant =
        this.protectionVariantId > 0 && Number(item.variant_id) === this.protectionVariantId;
      const isProtectionProperty = String(item.properties?._order_protection || '') === 'true';
      const isExplicitlyExcluded =
        String(item.properties?._exclude_from_shipping_reward || '') === 'true';

      return isProtectionVariant || isProtectionProperty || isExplicitlyExcluded;
    }

    qualifyingTotal(cart) {
      const excludedTotal = (cart.items || []).reduce((total, item) => {
        return this.isExcludedItem(item) ? total + Number(item.final_line_price || 0) : total;
      }, 0);

      return Math.max(0, Number(cart.total_price || 0) - excludedTotal);
    }

    hasSubscription(cart) {
      return (cart.items || []).some(
        (item) => Number(item.quantity || 0) > 0 && Boolean(item.selling_plan_allocation?.selling_plan?.id)
      );
    }

    renderCart(cart) {
      // Inspect the whole cart, not an add response (which contains only added lines).
      this.hidden = this.hasSubscription(cart);
      this.render(this.qualifyingTotal(cart));
    }

    formatMoney(cents) {
      const amount = Math.max(0, cents) / 100;
      const hasCents = Math.round(amount * 100) % 100 !== 0;

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: hasCents ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `${this.currency} ${amount.toFixed(hasCents ? 2 : 0)}`;
      }
    }

    render(qualifyingTotal) {
      if (!this.threshold) return;

      const remaining = Math.max(0, this.threshold - qualifyingTotal);
      const reached = remaining === 0;
      const percent = Math.min(100, (qualifyingTotal / this.threshold) * 100);

      this.dataset.reached = String(reached);

      if (this.beforeMessage) this.beforeMessage.hidden = reached;
      if (this.reachedMessage) this.reachedMessage.hidden = !reached;
      if (this.remainingAmount) this.remainingAmount.textContent = this.formatMoney(remaining);
      if (this.fill) this.fill.style.width = `${percent}%`;

      if (this.progress) {
        this.progress.setAttribute('aria-valuenow', String(Math.min(qualifyingTotal, this.threshold)));
      }
    }

    async sync() {
      if (!this.isConnected || !this.threshold) return;
      const version = this.syncVersion = (this.syncVersion || 0) + 1;

      try {
        const response = await fetch(cartUrl, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

        const cart = await response.json();
        if (!this.isConnected || version !== this.syncVersion) return;
        this.renderCart(cart);
      } catch (error) {
        console.warn('[Shipping reward] Could not refresh the progress bar.', error);
      }
    }
  }

  customElements.define('shipping-reward', ShippingReward);

  let syncTimer;
  const scheduleSync = () => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      document.querySelectorAll('shipping-reward').forEach((reward) => reward.sync());
    }, 100);
  };

  [
    'cart:refresh',
    'cart:updated',
    'cart:change',
    'cart:item-added',
    'line-item:change:end',
    'cart-drawer:open',
    'order-protection:changed',
  ].forEach((eventName) => document.addEventListener(eventName, scheduleSync));

  document.addEventListener('purty:subscription-bundle-added', () => {
    // Emitted only after a successful subscription add. Hide immediately, then
    // reconcile against the full cart (including removal/quantity corrections).
    document.querySelectorAll('shipping-reward').forEach((reward) => {
      reward.syncVersion = (reward.syncVersion || 0) + 1;
      reward.hidden = true;
    });
    scheduleSync();
  });
  window.addEventListener('pageshow', scheduleSync);
})();
